import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useOpenCardPage } from "@/api/hooks/use-billing-profile";
import { useLifecycle } from "@/api/hooks/use-lifecycle";
import { toUserMessage } from "@/lib/errors";

const addCardCls =
  "rounded-md bg-electric-blue px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-electric-blue/90";

/** Shown on every page when a Self-serve Customer's Free week has ended with
 * no card on file (ticket 19): during Payment grace, the days until requests
 * are Blocked; once Blocked, what happened. Either way, an admin's Add card
 * opens the one-step card page (ticket 20). */
export function CardNeededBanner() {
  const { user } = useAuth();
  const { data } = useLifecycle();
  const openCardPage = useOpenCardPage();
  const [error, setError] = useState("");
  // By the Payment grace end, not the reason: Paused doesn't stop its clock.
  const grace = data?.state !== "blocked" && data?.payment_grace_days_left != null;
  const blocked = data?.state === "blocked" && data.reason === "no_card";
  if (!data || (!grace && !blocked)) return null;

  const days = data.payment_grace_days_left ?? 0;
  return (
    <div
      data-testid="card-needed-banner"
      className={`mb-6 flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between ${
        blocked ? "border-block-red/40 bg-block-red/10" : "border-amber-400/50 bg-amber-400/10"
      }`}
    >
      <div className="space-y-1 text-sm">
        {blocked ? (
          <>
            <p className="font-medium text-fg">Your requests are blocked.</p>
            <p className="text-fg-muted">
              Your Free week ended with no card on file. Phantm is refusing requests until a card is added; they work
              again as soon as it is.
            </p>
          </>
        ) : (
          <>
            <p className="font-medium text-fg">
              Your Free week has ended. Add a card within {days} {days === 1 ? "day" : "days"}, or requests will be
              blocked.
            </p>
            <p className="text-fg-muted">
              Until then, requests go to your provider exactly as sent: nothing is optimized and no fee is charged.
            </p>
          </>
        )}
      </div>
      {user?.role === "admin" ? (
        <div className="flex shrink-0 flex-col items-end gap-1">
          <button
            type="button"
            onClick={() => openCardPage.mutate(undefined, { onError: (err) => setError(toUserMessage(err)) })}
            disabled={openCardPage.isPending}
            className={addCardCls}
          >
            {openCardPage.isPending ? "Opening Stripe…" : "Add card"}
          </button>
          {error && <p className="text-xs text-block-red">{error}</p>}
        </div>
      ) : (
        <p className="shrink-0 text-sm text-fg-muted">An admin can add a card in Billing.</p>
      )}
    </div>
  );
}
